import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FiInfo } from "react-icons/fi";

/**
 * ⓘ icon revealing a deep usage note on hover (and on keyboard focus). Sits next
 * to a field label: the short `description` under the input says WHAT to enter,
 * this tooltip says exactly how the site USES the value (see fieldUsageNotes.ts).
 *
 * The bubble is rendered through a portal in `document.body` with a fixed
 * position : an absolutely-positioned child would be clipped by the modal's
 * scrolling body, cutting the text at the modal border.
 */
const TIP_WIDTH = 288; // w-72
const EDGE_MARGIN = 12;

const FieldInfoTip: React.FC<{ note?: string | null }> = ({ note }) => {
	const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
	if (!note?.trim()) return null;

	const show = (target: HTMLElement) => {
		const rect = target.getBoundingClientRect();
		const half = TIP_WIDTH / 2;
		const x = Math.min(Math.max(rect.left + rect.width / 2, EDGE_MARGIN + half), window.innerWidth - EDGE_MARGIN - half);
		setAnchor({ x, y: rect.bottom });
	};

	return (
		<span
			className="inline-flex"
			tabIndex={0}
			aria-label="En savoir plus sur ce champ"
			onMouseEnter={(event) => show(event.currentTarget)}
			onMouseLeave={() => setAnchor(null)}
			onFocus={(event) => show(event.currentTarget)}
			onBlur={() => setAnchor(null)}
		>
			<FiInfo className="h-3.5 w-3.5 cursor-help text-slate-400 transition hover:text-slate-600" aria-hidden="true" />
			{anchor &&
				createPortal(
					<span
						role="tooltip"
						// z-[3000]: above every modal layer of the app (modals are z-[1300], the
						// highest fixed overlays z-[2000]) : a tooltip must never sit behind the
						// surface that contains its icon.
						className="pointer-events-none fixed z-[3000] w-72 -translate-x-1/2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-[11px] font-normal normal-case leading-4 tracking-normal text-slate-100 shadow-xl"
						style={{ left: anchor.x, top: anchor.y + 6 }}
					>
						{note}
					</span>,
					document.body
				)}
		</span>
	);
};

export default FieldInfoTip;
